/**
 * Centralized auth error handler.
 *
 * Catches 401 responses from both axios and native fetch calls,
 * clears all auth state, and redirects to login.
 * Debounced so concurrent 401s don't trigger multiple redirects.
 */

import { useUserStore } from '../stores/user'
import { clearDevToken } from '../composables/useAuth'
import router from '../router'

let isRedirecting = false

/**
 * Call this when any API call returns 401 Unauthorized.
 * During impersonation, redirects to /admin (back to the admin session).
 * Otherwise, clears user/token state and redirects to /login.
 * Debounced so concurrent 401s don't trigger multiple redirects.
 */
export function handleAuthError() {
    if (isRedirecting) return
    isRedirecting = true

    const userStore = useUserStore()

    // If impersonating, end the impersonation session and go back to admin
    if (userStore.isImpersonating) {
        localStorage.removeItem('impersonation_token')
        localStorage.removeItem('impersonation_original_token')
        // Hard redirect to /admin to fully reset state
        window.location.href = '/admin'
        // Reset flag (won't execute due to navigation, but for safety)
        setTimeout(() => { isRedirecting = false }, 2000)
        return
    }

    // Normal 401: clear everything and redirect to login
    userStore.clearUser()
    clearDevToken()
    localStorage.removeItem('impersonation_token')
    localStorage.removeItem('impersonation_original_token')

    router.push('/login')

    // Reset flag after a short delay so future 401s (in a new session) still work
    setTimeout(() => {
        isRedirecting = false
    }, 2000)
}

/**
 * Call this when any API call returns 403 with code ACCOUNT_SUSPENDED.
 * The backend has suspended this account; clear all auth state and send the
 * user to /login with a message. During impersonation (which the backend does
 * NOT suspend), fall back to ending the impersonation session like a 401.
 * Shares the redirect debounce with handleAuthError.
 */
export function handleAccountSuspended() {
    if (isRedirecting) return
    isRedirecting = true

    const userStore = useUserStore()

    if (userStore.isImpersonating) {
        localStorage.removeItem('impersonation_token')
        localStorage.removeItem('impersonation_original_token')
        window.location.href = '/admin'
        setTimeout(() => { isRedirecting = false }, 2000)
        return
    }

    userStore.clearUser()
    clearDevToken()
    localStorage.removeItem('impersonation_token')
    localStorage.removeItem('impersonation_original_token')

    router.push('/login?error=suspended')

    setTimeout(() => {
        isRedirecting = false
    }, 2000)
}

/**
 * Check if a JWT token is expired (or within `bufferSeconds` of expiry).
 * Returns true if expired/invalid, false if still valid.
 */
export function isTokenExpired(token: string, bufferSeconds = 60): boolean {
    try {
        const parts = token.split('.')
        if (parts.length < 2) return true

        const payload = parts[1]!
        const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))

        if (!decoded.exp) return false // No expiry claim — treat as valid

        const expiresAt = decoded.exp * 1000 // Convert to ms
        return Date.now() >= expiresAt - bufferSeconds * 1000
    } catch {
        return true // Can't decode — treat as expired
    }
}
